import React, { useState, useEffect } from 'react';
import { medicPortalAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  CalendarDays, 
  Clock,
  MapPin,
  Search,
  Send,
  CheckCircle,
  XCircle,
  Loader2,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const TYPE_STYLES = {
  regular: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  night: 'bg-indigo-500/15 text-indigo-400',
  standby: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  emergency: 'bg-red-500/15 text-red-400',
};

const REQUEST_STATUS_STYLES = {
  requested: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  declined: 'bg-red-500/15 text-red-400 border-red-500/20',
};

export default function OpenShifts() {
  const [openShifts, setOpenShifts] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestingShiftId, setRequestingShiftId] = useState(null);
  const [searchLocation, setSearchLocation] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [shiftsRes, requestsRes] = await Promise.all([
        medicPortalAPI.getOpenShifts(),
        medicPortalAPI.getMyRequests(),
      ]);
      setOpenShifts(shiftsRes.data);
      setMyRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestShift = async (shiftId) => {
    setRequestingShiftId(shiftId);
    try {
      const response = await medicPortalAPI.requestShift({ shift_id: shiftId });
      
      // Check if there's a conflict warning
      if (response.data.has_conflicts && response.data.warning_message) {
        toast.warning(response.data.warning_message, { duration: 8000 });
      } else {
        toast.success('Shift request submitted successfully');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request shift');
    } finally {
      setRequestingShiftId(null);
    }
  };

  const filteredShifts = openShifts.filter(shift => {
    if (!searchLocation) return true;
    return shift.location.toLowerCase().includes(searchLocation.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  const ShiftCard = ({ shift }) => (
    <Card className="bg-[#2B2B2B] border-white/5 hover:border-[#1ABC9C]/20 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${TYPE_STYLES[shift.shift_type]} border-0`}>
                {shift.shift_type}
              </Badge>
              {shift.job_type_name && (
                <Badge className="bg-white/5 text-[#A1A1AA] border-0">
                  <Briefcase className="w-3 h-3 mr-1" />
                  {shift.job_type_name}
                </Badge>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[#F2F2F2]">
                <CalendarDays className="w-4 h-4 text-[#1ABC9C]" />
                <span className="font-medium">
                  {format(new Date(shift.date), 'EEEE, MMMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Clock className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">
                  {shift.start_time} - {shift.end_time}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <MapPin className="w-4 h-4" />
                <span>{shift.location}</span>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            {shift.already_requested ? (
              <Badge className={REQUEST_STATUS_STYLES.requested}>
                <Clock className="w-3 h-3 mr-1" />
                Requested
              </Badge>
            ) : (
              <Button
                size="sm"
                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
                onClick={() => handleRequestShift(shift.id)}
                disabled={requestingShiftId === shift.id}
                data-testid={`request-shift-${shift.id}`}
              >
                {requestingShiftId === shift.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1" />
                    Request
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const RequestCard = ({ request }) => (
    <Card className="bg-[#2B2B2B] border-white/5">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Badge className={REQUEST_STATUS_STYLES[request.status]}>
                {request.status === 'requested' && <Clock className="w-3 h-3 mr-1" />}
                {request.status === 'approved' && <CheckCircle className="w-3 h-3 mr-1" />}
                {request.status === 'declined' && <XCircle className="w-3 h-3 mr-1" />}
                {request.status}
              </Badge>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-[#F2F2F2]">
                <CalendarDays className="w-4 h-4 text-[#1ABC9C]" />
                <span className="font-medium">
                  {request.shift_date ? format(new Date(request.shift_date), 'EEE, MMM dd, yyyy') : 'N/A'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Clock className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">
                  {request.shift_start_time} - {request.shift_end_time}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <MapPin className="w-4 h-4" />
                <span>{request.shift_location || 'N/A'}</span>
              </div>
            </div>
          </div>
          
          <div className="text-right text-sm text-[#A1A1AA]">
            <p>Requested</p>
            <p className="font-['JetBrains_Mono']">
              {format(new Date(request.created_at), 'MMM dd, HH:mm')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="open-shifts-container">
      <Tabs defaultValue="browse" className="w-full">
        <TabsList className="bg-[#2B2B2B] border-white/5">
          <TabsTrigger 
            value="browse" 
            className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]"
            data-testid="browse-shifts-tab"
          >
            Browse Open Shifts ({filteredShifts.length})
          </TabsTrigger>
          <TabsTrigger 
            value="requests" 
            className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]"
            data-testid="my-requests-tab"
          >
            My Requests ({myRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <Input
              placeholder="Search by location..."
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="pl-10 bg-[#2B2B2B] border-white/10 text-[#F2F2F2] placeholder:text-[#A1A1AA]"
              data-testid="search-location-input"
            />
          </div>

          {/* Shifts List */}
          {filteredShifts.length === 0 ? (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-8 text-center">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA] opacity-50" />
                <p className="text-[#A1A1AA]">
                  {openShifts.length === 0 
                    ? 'No open shifts available at the moment' 
                    : 'No shifts match your search'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredShifts.map(shift => (
                <ShiftCard key={shift.id} shift={shift} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-6">
          {myRequests.length === 0 ? (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-8 text-center">
                <Send className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA] opacity-50" />
                <p className="text-[#A1A1AA]">You haven't requested any shifts yet</p>
                <p className="text-sm text-[#A1A1AA] mt-2">
                  Browse open shifts and request ones that match your availability
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {myRequests.map(request => (
                <RequestCard key={request.id} request={request} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
